import { useQuery } from '@tanstack/react-query'

import { internalApi } from '@/modules/api/request'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'

/**
 * Feature flags that the code reads through this constant. A flag name that is a bare string
 * gives no compiler error when it has a typo or when the flag is removed.
 */
export const FEATURE_FLAGS = {
  /** The new Mobility Manager theme (header filters, metrics pop-up) instead of the old UI. */
  MOBILITY_THEME: 'mobility_theme',
} as const

export const featureFlagsQueryObject = {
  queryKey: ['/feature_flags'],
  queryFn: async () => await internalApi.featureFlags.getFeatureFlags(),
}

export const useFeatureFlags = () => useQuery(featureFlagsQueryObject)

/**
 * Whether a flag is on for the current region, and whether the flags are still loading.
 * Use this instead of `useFeatureFlag` when "loading" and "off" must not look the same, for
 * example to choose between two page layouts.
 */
export const useFeatureFlagStatus = (
  flagName: string
): { isEnabled: boolean; isPending: boolean } => {
  const { data: featureFlags, isSuccess, isPending } = useFeatureFlags()

  const {
    data: { regionId },
  } = useCurrentRegion()

  const isEnabled =
    isSuccess &&
    featureFlags
      .filter(flag => flag.region_id === regionId || !flag.region_id)
      .some(flag => flag.feature_slug === flagName)

  return { isEnabled, isPending }
}

export const useFeatureFlag = (flagName: string) => useFeatureFlagStatus(flagName).isEnabled
